import { cn } from "@/lib/utils";
import { Star, Layers, FlaskConical, BarChart3, MoreVertical, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TableCell, TableRow } from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TruncatedText } from "@/components/ui/truncated-text";
import { AmazonSearchLink } from "@/components/shared/AmazonSearchLink";
import { ThemeColumnCell, ThemePickerSubmenu } from "../KeywordThemeShared";
import { KeywordMetricCells, KeywordTagCells } from "../KeywordMetricCells";
import { Keyword, UtilizationTag } from "@/types";
import { KeywordMasterListAction } from "@/constants";

interface KeywordBankKeywordRowProps {
  keyword: Keyword;
  /** `landscape` drops Theme, Utilization and Source. See `KeywordBankTableHeader`. */
  variant?: "bank" | "landscape";
  /**
   * AUT-496. False only where the values cannot be there: the public share payload leaves the
   * SERP Match fields out, so the column would be dashes for every row forever.
   */
  showSerpMatch?: boolean;
  /** This screen's row actions. Omitted, the Keyword Bank's own menu renders. */
  renderActions?: (keyword: Keyword) => React.ReactNode;
  isSelected: boolean;
  readOnly: boolean;
  stickyStyle: React.CSSProperties;
  stickyGrid: string;
  keywordTextWidth: number;
  themes: { id: number; name: string }[];
  keywordThemeMap?: Map<number, { themeId: number; themeName: string }>;
  heroAsinData: unknown;
  onSelectKeyword: (keywordId: number, selected: boolean) => void;
  onUpdateMasterList: (keywordIds: number[], action: KeywordMasterListAction) => void;
  onAddKeywordToTheme?: (keywordId: number, themeId: number) => void;
  onChangeKeywordTheme?: (keywordId: number, fromThemeId: number, toThemeId: number) => void;
  onOpenAddTheme?: (keywordId?: number) => void;
  onOpenLandscape?: (landscapeId: number) => void;
  onCreateLandscape: (searchPhrase: string) => void;
  onAddToRankTracker: (keyword: Keyword) => void;
  onRemoveFromRankTracker: (keyword: Keyword) => void;
  onNavigateToAbTest: (keyword: Keyword) => void;
  onRequestDelete: (id: number, search: string) => void;
  onRequestUnassign: (keywordId: number, themeId: number, search: string, themeName: string) => void;
}

function getMasterListActionLabel(isMasterList: boolean) {
  return isMasterList ? "Delete from Master list" : "Add to Master Keyword List";
}

export function KeywordBankKeywordRow({
  keyword,
  variant = "bank",
  showSerpMatch = true,
  renderActions,
  isSelected,
  readOnly,
  stickyStyle,
  stickyGrid,
  keywordTextWidth,
  themes,
  keywordThemeMap,
  heroAsinData,
  onSelectKeyword,
  onUpdateMasterList,
  onAddKeywordToTheme,
  onChangeKeywordTheme,
  onOpenAddTheme,
  onOpenLandscape,
  onCreateLandscape,
  onAddToRankTracker,
  onRemoveFromRankTracker,
  onNavigateToAbTest,
  onRequestDelete,
  onRequestUnassign,
}: KeywordBankKeywordRowProps) {
  const toggleMasterList = () => {
    onUpdateMasterList(
      [keyword.id],
      keyword?.is_master_list ? KeywordMasterListAction.REMOVE : KeywordMasterListAction.ADD,
    );
  };

  return (
    <TableRow
      key={keyword.id}
      data-state={isSelected ? "selected" : undefined}
      className={cn(isSelected && "bg-primary/5")}
    >
      <TableCell sticky style={stickyStyle} className="py-0 px-0">
        <div className={stickyGrid}>
          {!readOnly && (
            <span className="py-2 px-2 flex items-center justify-center">
              <Checkbox
                checked={isSelected}
                onCheckedChange={(checked) => onSelectKeyword(keyword.id, !!checked)}
                aria-label={`Select ${keyword.search}`}
              />
            </span>
          )}
          <span className="py-2 flex items-center justify-center">
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  {readOnly ? (
                    <span className="inline-flex">
                      <Star className={cn("h-4 w-4", keyword?.is_master_list ? "text-amber-400 fill-current" : "text-muted-foreground/40")} aria-hidden />
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={toggleMasterList}
                      className={cn("transition-colors", keyword?.is_master_list ? "text-amber-400 hover:text-amber-500" : "text-muted-foreground/40 hover:text-amber-400")}
                      aria-label={getMasterListActionLabel(keyword?.is_master_list)}
                    >
                      <Star className={cn("h-4 w-4", keyword?.is_master_list && "fill-current")} />
                    </button>
                  )}
                </TooltipTrigger>
                <TooltipContent>
                  {getMasterListActionLabel(keyword?.is_master_list)}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </span>
          {/* AUT-143/144: explicit width (not self-observed) so this
              re-measures on column resize without adding a ResizeObserver
              per row -- see TruncatedText and index.tsx. */}
          <span className="py-2 px-3 text-sm font-medium flex items-center gap-2 min-w-0">
            <TruncatedText width={keywordTextWidth} className="font-medium">
              {keyword.search}
            </TruncatedText>
            {/* AUT-496: pinned right so every row's icon lands in one column. */}
            <AmazonSearchLink keyword={keyword.search} className="order-last ml-auto" />
          </span>
        </div>
      </TableCell>
      <KeywordMetricCells keyword={keyword} variant={variant} showSerpMatch={showSerpMatch} />
      {variant === "bank" && (
        <>
          <TableCell className="w-[115px] py-2 px-2">
            <div className="flex items-center justify-center">
              <ThemeColumnCell
                themeName={keywordThemeMap?.get(keyword.id)?.themeName}
                themes={themes}
                keywordId={keyword.id}
                onSelect={(themeId) => onAddKeywordToTheme?.(keyword.id, themeId)}
                onOpenAddTheme={onOpenAddTheme}
                readOnly={readOnly}
              />
            </div>
          </TableCell>
          <KeywordTagCells keyword={keyword} onOpenLandscape={onOpenLandscape} />
        </>
      )}
      {!readOnly && (
        <TableCell className="w-[70px] py-1.5 px-2">
          <div className="flex items-center justify-center">
            {/* Each screen owns its row actions -- the Keyword Bank offers "Add Landscape" and
                "Run A/B Test"; a landscape's menu is its own. Everything else about the row is
                shared, so the menu is injected rather than made conditional on the variant. */}
            {renderActions ? renderActions(keyword) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6">
                  <MoreVertical className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-popover z-50 rounded-[8px]">
                <DropdownMenuItem onClick={() => onCreateLandscape(keyword.search)}>
                  <Layers className="h-4 w-4 mr-2" />
                  Add Landscape
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={!heroAsinData}
                  onClick={() => onNavigateToAbTest(keyword)}
                >
                  <FlaskConical className="h-4 w-4 mr-2" />
                  Run A/B Test
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() =>
                    keyword.utilizationTags?.includes(UtilizationTag.TRACKED)
                      ? onRemoveFromRankTracker(keyword)
                      : onAddToRankTracker(keyword)
                  }
                >
                  <BarChart3 className="h-4 w-4 mr-2" />
                  {keyword.utilizationTags?.includes(UtilizationTag.TRACKED)
                    ? "Remove from Rank Tracker"
                    : "Add to Rank Tracker"}
                </DropdownMenuItem>

                {themes.length > 0 && (
                  <>
                    <DropdownMenuSeparator />
                    <ThemePickerSubmenu
                      themes={themes}
                      currentThemeId={keywordThemeMap?.get(keyword.id)?.themeId}
                      onSelect={(toThemeId) => {
                        const current = keywordThemeMap?.get(keyword.id);
                        if (current) {
                          onChangeKeywordTheme?.(keyword.id, current.themeId, toThemeId);
                        } else {
                          onAddKeywordToTheme?.(keyword.id, toThemeId);
                        }
                      }}
                      onRemove={() => {
                        const current = keywordThemeMap?.get(keyword.id);
                        if (current) {
                          onRequestUnassign(keyword.id, current.themeId, keyword.search, current.themeName);
                        }
                      }}
                    />
                  </>
                )}

                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={toggleMasterList}>
                  <Star className="h-4 w-4 mr-2" />
                  {getMasterListActionLabel(keyword?.is_master_list)}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => onRequestDelete(keyword.id, keyword.search)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete keyword
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            )}
          </div>
        </TableCell>
      )}
    </TableRow>
  );
}
